import React, { useState, useEffect } from 'react';
import { Receipt, Search, RefreshCw, Download, Eye, User, AlertTriangle, X, CheckCircle, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import MainService from '../../services/MainService';
import * as XLSX from 'xlsx';

const TrackByBill = () => {
  const navigate = useNavigate();
  const [billNo, setBillNo] = useState('');
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [warning, setWarning] = useState('');

  // Toast notification effects
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (warning) {
      const timer = setTimeout(() => {
        setWarning('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [warning]);

  const handleSearch = async () => {
    const billNumbers = billNo.split('.').filter(v => v.trim() !== '');
    
    if (billNumbers.length === 0) {
      setWarning('Please enter valid bill numbers');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      setWarning('');

      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN') || localStorage.getItem('userInfo');
      if (!tokenData) {
        setError('Please login first');
        return;
      }

      const userData = typeof tokenData === 'string' ? JSON.parse(tokenData) : tokenData;

      const data = {
        VoucherNos: billNumbers,
      };

      const token = userData.Token || localStorage.getItem('authToken');
      const response = await MainService.TrackOrdersByBillNosPortal(data, token);

      if (response && response.length > 0) {
        setOrders(response);
        setSuccess(`Found ${response.length} order(s) for bill number "${billNo}"`);
      } else {
        setOrders([]);
        setWarning(`No orders found for bill number "${billNo}"`);
      }
    } catch (error) {
      console.error('Error searching orders:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClear = () => {
    setBillNo('');
    setOrders([]);
    setError('');
    setSuccess('');
    setWarning('');
  };

  const exportToExcel = () => {
    if (orders.length === 0) {
      setWarning('No orders to export');
      return;
    }

    try {
      const exportData = orders.map((order, index) => ({
        '#': index + 1,
        'Bill Number': order.ClientBillNo || 'N/A',
        'Voucher No': order.VoucherNo || 'N/A',
        'Customer Name': order.Customer?.Name || 'N/A',
        'Mobile No1': order.Customer?.MobileNo1 || 'N/A',
        'Mobile No2': order.Customer?.MobileNo2 || 'N/A',
        'Status': order.Status?.Name || 'N/A',
        'Address': order.CustomerAddress?.Address || 'N/A',
        'Location': order.CustomerAddress?.Location?.Name || 'N/A',
        'Emirate': order.CustomerAddress?.Emirate?.Name || 'N/A',
        'Order Date': order.Date ? new Date(order.Date).toLocaleDateString('en-GB') : 'N/A',
        'Pickup Date': order.PickupDate ? new Date(order.PickupDate).toLocaleDateString('en-GB') : 'N/A',
        'Delivery Date': order.DeliveryDate ? new Date(order.DeliveryDate).toLocaleDateString('en-GB') : 'N/A',
        'Product Price': order.ProductPrice ? `AED ${parseFloat(order.ProductPrice).toFixed(2)}` : 'N/A',
        'Delivery Charge': order.DeliveryCharge ? `AED ${parseFloat(order.DeliveryCharge).toFixed(2)}` : 'N/A',
        'Tax': order.TAX ? `AED ${parseFloat(order.TAX).toFixed(2)}` : 'N/A',
        'Collection Amount': order.CustomerAmount ? `AED ${parseFloat(order.CustomerAmount).toFixed(2)}` : 'N/A',
        'Client Amount': order.ClientAmount ? `AED ${parseFloat(order.ClientAmount).toFixed(2)}` : 'N/A'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Search Results');
      const fileName = `bill_search_results_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setSuccess('Excel file exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export to Excel');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    if (amount != null && amount !== '') {
      return `AED ${parseFloat(amount).toFixed(2)}`;
    }
    return 'N/A';
  };

  const getStatusChipStyle = (color) => {
    if (!color) {
      return {
        backgroundColor: '#E5E7EB',
        color: '#374151'
      };
    }
    
    let backgroundColor = color;
    let textColor = '#FFFFFF';
    
    // Light colors need dark text
    if (color.toLowerCase().includes('yellow') || color.toLowerCase().includes('light')) {
      textColor = '#000000';
    }
    
    return {
      backgroundColor,
      color: textColor
    };
  };

  if (isLoading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-primary">Searching orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {warning && (
        <div className="fixed top-20 right-4 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{warning}</span>
          <button onClick={() => setWarning('')} className="text-yellow-500 hover:text-yellow-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center mb-2">
            <Receipt className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">Track By Bill</h1>
          </div>
          <p className="text-sm text-gray-500 ml-8">Search orders by client bill number</p>
        </div>

        {/* Search Section */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bill Number Search
            </label>
            <input
              type="text"
              value={billNo}
              onChange={(e) => setBillNo(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter bill number"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="flex items-end gap-3">
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="bg-primary hover:bg-primary/90 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
            >
              <Search className="w-4 h-4" />
              <span>Search</span>
            </button>
            <button
              onClick={handleClear}
              className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Clear</span>
            </button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {orders.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center">
              <List className="w-5 h-5 text-primary mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">
                Search Results ({orders.length} orders found)
              </h3>
            </div>
            <button
              onClick={exportToExcel}
              className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors flex items-center gap-2"
              title="Export Excel"
            >
              <Download className="w-5 h-5" />
              <span className="hidden sm:inline">Export Excel</span>
            </button>
          </div>

          <TableContainer 
            component={Paper} 
            elevation={0}
            sx={{ 
              height: '500px',
              position: 'relative',
              border: '1px solid #E5E7EB',
              borderRadius: '0.5rem',
              '& .MuiPaper-root': {
                boxShadow: 'none'
              }
            }}
          >
            {isLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.75)',
                  backdropFilter: 'blur(4px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 20
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={32} sx={{ color: '#46955A' }} />
                  <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Loading...</span>
                </Box>
              </Box>
            )}
            <Table stickyHeader sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '5%' }}>#</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '15%', minWidth: '150px' }}>Bill Number</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '15%', minWidth: '150px' }}>Voucher No</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB' }}>Client</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB' }}>Customer</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB' }}>Status</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB' }}>Address</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB' }}>Location</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB' }}>Emirate</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB' }}>Order Date</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB' }}>Delivery Date</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB' }}>Collection Amount</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB' }}>Client Amount</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order, index) => (
                  <TableRow
                    key={index}
                    hover
                    sx={{
                      '&:hover': { backgroundColor: '#F9FAFB' },
                      '&:last-child td, &:last-child th': { border: 0 }
                    }}
                  >
                    <TableCell align="center" sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10B981' }} />
                        {order.ClientBillNo || 'N/A'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151' }}>{order.VoucherNo || 'N/A'}</TableCell>
                    <TableCell>
                        <Box>
                          <Box sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
                            {order.Client?.Name || 'N/A'}
                          </Box>
                          <Box sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
                            {order.Client?.MobileNo1 || 'N/A'}
                          </Box>
                        </Box>
                      </TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
                          {order.Customer?.Name || 'N/A'}
                        </Box>
                        <Box sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
                          {order.Customer?.MobileNo1 || 'N/A'}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.Status?.Name || 'N/A'}
                        size="small"
                        sx={{
                          ...getStatusChipStyle(order.Status?.Color),
                          fontSize: '0.75rem',
                          height: '24px'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {order.CustomerAddress?.Address || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151' }}>
                      {order.CustomerAddress?.Location?.Name || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151' }}>
                      {order.CustomerAddress?.Emirate?.Name || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151' }}>
                      {formatDate(order.Date)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151' }}>
                      {formatDate(order.DeliveryDate)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                        {order.CustomerAmount != null && order.CustomerAmount !== '' ? `AED ${parseFloat(order.CustomerAmount).toFixed(2)}` : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                        {order.ClientAmount != null && order.ClientAmount !== '' ? `AED ${parseFloat(order.ClientAmount).toFixed(2)}` : 'N/A'}
                      </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Order Details">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/home/orders/${order.Id}`)}
                          sx={{
                            color: '#2563EB',
                            '&:hover': { backgroundColor: '#DBEAFE' }
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>Total Orders: {orders.length}</span>
              <span>Showing {orders.length} order(s)</span>
            </div>
          </div>
        </div>
      ) : (
        /* Message Box when no orders */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-blue-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Search by Bill Number</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Enter bill number to find orders. You can search by partial bill numbers or complete bill identifiers.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <h4 className="font-medium text-gray-900 mb-2">Search Examples:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Bill Number: "INV-2024-001"</li>
                  <li>• Invoice ID: "12345"</li>
                  <li>• Reference: "REF-ABC-123"</li>
                  <li>• Partial bill: "INV-2024"</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <h4 className="font-medium text-gray-900 mb-2">What you'll see:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• All orders for the bill number</li>
                  <li>• Order status and dates</li>
                  <li>• Delivery addresses</li>
                  <li>• Payment information</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackByBill;
